import React, { useState, ComponentProps } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  PlayArrow as PlayArrowIcon
} from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import Editor from 'react-simple-code-editor';
import prism from 'prismjs';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import SplitPane from 'react-split-pane';

import { cleanCode } from '../../../libs/cleaner';
import Spec from '../../../models/Spec';
import TestOutputs, { Output as TestOutput, Props as TestOutputsProp } from '../../TestOutputs';
import utils from '../../../libs/utils';
import auth from '../../../libs/auth';
import { useDialog } from '../../../context/Dialog';
import { CSlack } from '../../Icons';


interface SpecTestProps extends Pick<TestOutputsProp, 'activityIndex' | 'onMarkAsCorrect' | 'onMarkAllAsCorrect'>{
  spec: Spec
  answer: string
  graded?: boolean
  onAnswerChange: (answer: string) => any
  onTestResultChange?: (result: TestResult | null) => any
  onBeforeTest?: () => any
  showReference?: boolean,
  courseId?: string
  courseWorkId?: string
  editorFooter?: ComponentProps<any>['children']
  resultFooter?: ComponentProps<any>['children']
}

export interface TestResult {
  id: string
  output: TestOutput[]
}

const useStyle = makeStyles(() => ({
  contentBox: {
    minWidth: '415px',
    maxHeight: '600px',
    overflow: 'auto'
  },
  editor: {
    display: 'flex',
    flex: '1 0 auto',

    '& pre': {
      width: '100%',
      display: 'block !important'
    }
  },
  splitPane: {
    '& .Resizer': {
      position: 'relative',
      opacity: 1,
      background: 'rgba(0,0,0,0.2)',
      backgroundClip: 'padding-box',

      '&.vertical:hover': {
        borderLeft: '5px solid rgba(0,0,0,0.1)',
        borderRight: '5px solid rgba(0,0,0,0.1)'
      },

      '&:before': {
        content: '""',
        display: 'block',
        height: '40px',
        width: '20px',
        backgroundColor: 'white',
        border: '1px solid rgba(0,0,0,0.2)',
        position: 'absolute',
        top: '50%',
        left: '-9.5px',
        transform: 'translateY(-50%)',
        borderRadius: '50px'
      },

      '&:after': {
        content: '""',
        display: 'block',
        height: '15px',
        width: '15px',
        backgroundImage: 'url(/assets/svg-icons/horizontal-resize.svg)',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        position: 'absolute',
        opacity: '0.5',
        top: '50%',
        left: '-7px',
        transform: 'translateY(-50%)',
        borderRadius: '50px'
      }
    }
  }
}));

export const SpecTest = (props: SpecTestProps) => {
  const {
    spec,
    answer,
    graded,
    onAnswerChange,
    onTestResultChange,
    onBeforeTest,
    showReference = false,
    courseId,
    courseWorkId,
    editorFooter,
    resultFooter,

    activityIndex,
    onMarkAllAsCorrect,
    onMarkAsCorrect
  } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const { alert } = useDialog();
  const classes = useStyle();

  const runTest = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      setTestResult(null);

      if (onBeforeTest)
        await onBeforeTest();

      const { data } = await axios.post('/exam/submit', JSON.stringify({
        submitterID: courseId && courseWorkId ? utils.generateSubmissionUID(courseId, courseWorkId) : uuid(),
        assignmentID: courseWorkId,
        activityID: spec.id,
        code: answer,
        isAdmin: auth.isAdmin(),
        accountID: auth.getSession()?.user?.userid.toString(),
        graded,
      }));

      data.output = data.output || [];
      setTestResult(data as TestResult);
      if (onTestResultChange)
        onTestResultChange(data as TestResult);
    }
    catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    }
    finally {
      setIsLoading(false);
    }
  };

  const shareRefToSlack = async (ref: string) => {
    await navigator.clipboard.writeText([
      'Hello. I need assistance with my code:',
      `\`${ref}\`\n`,
      'The problem that I\'m having is:',
      '<Describe your problem here>'
    ].join('\n'));

    await alert((
      <Typography>Reference code has been copied. Simply paste it in your message and send.</Typography>
    ), {
      title: 'Share Reference to Slack'
    });
    window.open('slack://open');
  };

  const showOutputColumn = !!testResult || !!errorMessage;

  const editorColumn = (
    <>
      <Box position="relative" display="flex" flexDirection="column" flexGrow={1} pb={2}>
        <Box className={classes.contentBox} display="flex" flexDirection="column" flexGrow={1}>
          <Editor
            value={answer}
            onValueChange={onAnswerChange}
            onBlur={(event: any) => onAnswerChange(cleanCode(event.target.value))}
            disabled={isLoading}
            highlight={(code) => prism.highlight(
              code,
              prism.languages.javascript,
              'javascript'
            )}
            padding={10}
            style={{
              fontFamily: '"Fira code", "Fira Mono", monospace',
              backgroundColor: 'rgba(250,250,250, 1)',
              fontSize: 12
            }}
            className={classes.editor}
            textareaClassName="box-border"
            preClassName="box-border"
            autoFocus
          />
        </Box>
      </Box>
      <Grid container justify="space-between" alignItems="center">
        <Grid item>{editorFooter}</Grid>
        <Grid item>
          <Button
            type="button"
            disabled={isLoading || !answer}
            startIcon={isLoading ? <CircularProgress size="1em" color="inherit" /> : <PlayArrowIcon />}
            onClick={() => runTest()}
            variant="contained"
            color="primary"
          >
            Run
          </Button>
        </Grid>
      </Grid>
    </>
  );

  const outputColumn = (
    <>
      <Box position="relative" display="flex" flexDirection="column" flexGrow={1} pb={2}>
        <Box className={classes.contentBox}>
          {!!testResult?.id && showReference && (
            <Box pb={1}>
              <Alert severity="info">
                <AlertTitle>Having issues?</AlertTitle>
                <Typography>
                  Submit the following reference code to #technical-questions on Slack for support:
                </Typography>
                <Typography>
                  <b>{ testResult.id }</b>
                  <span>&nbsp;&nbsp;</span>
                  <Button
                    startIcon={<CSlack />}
                    type="button"
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={() => shareRefToSlack(testResult?.id || '')}
                  >
                    Share to Slack
                  </Button>
                </Typography>
              </Alert>
            </Box>
          )}

          {(() => {
            if (errorMessage)
              return (
                <div className="text-center p-4">
                  <Typography variant="h5">Something went wrong.</Typography>
                  <p className="m-0">{ errorMessage }</p>
                  <p className="m-0">Please run test again.</p>
                </div>
              );

            if (testResult)
              return (
                <TestOutputs
                  activityIndex={activityIndex}
                  outputs={testResult.output}
                  onMarkAllAsCorrect={onMarkAllAsCorrect}
                  onMarkAsCorrect={onMarkAsCorrect}
                />
              );
          })()}
        </Box>
      </Box>

      <Grid container justify="space-between" alignItems="center">
        <div>{resultFooter}</div>

        <Button
          type="button"
          color="primary"
          variant="outlined"
          onClick={() => {
            setTestResult(null);
            setErrorMessage('');
            if (onTestResultChange)
              onTestResultChange(null);
          }}>
          Close
        </Button>
      </Grid>
    </>
  );

  if (showOutputColumn)
    return (
      <SplitPane style={{position: 'static'}}
                 paneStyle={{
                   display: 'flex',
                   maxWidth: '95%',
                   overflow: 'auto'
                 }}
                 className={classes.splitPane}
                 minSize={100}
                 defaultSize="50%"
                 split="vertical">
        <Box display="flex" flexDirection="column" flexGrow={1} pr={2}>{editorColumn}</Box>
        <Box display="flex" flexDirection="column" flexGrow={1} pl={2}>{outputColumn}</Box>
      </SplitPane>
    );

  return (<Box display="flex" flexDirection="column" flexGrow={1}>{editorColumn}</Box>);
};

export default SpecTest;
